import React, {useEffect, useState} from "react";
import {pageAuthorizedByGroup} from "../../fragments/PageAuthorization";
import {Link, useParams} from "react-router-dom";
import {useQuery} from "@apollo/client";
import {graphQLServices} from "../../../services/GraphQLServices";
import {GET_FAKE_COMPOSED_FORM, GET_FORM_DETAILS} from "./Queries";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Breadcrumbs, Button,
	CircularProgress, FormControl, InputLabel, MenuItem, Select, Stack,
	Tab,
	Tabs, TextField,
	Typography
} from "@mui/material";
import {FormSetup} from "../models/definitions/FormSetup";
import {PentaSplit} from "../../../components/common/PentaSplit";
import {DynamicFormsSetupEditorData} from "../editor/DynamicFormsSetupEditorData";
import {Allotment} from "allotment";
import {ExpandMore} from "@mui/icons-material";
import {ErrorBoundary} from "react-error-boundary";
import {generalError} from "../../fragments/ErrorElement";
import {newDefinition} from "../questions/DynamicFormsQuestionDetails";
import {deepClone, normalizeData} from "../../../libraries/util/Functions";
import {DynamicFormsFormEditor} from "../editor/DynamicFormsEditor";
import {QuestionFrontEndName, questionFrontEndNames} from "../models/definitions/QuestionFrontEndName";
import {QuestionClientName, questionClientNames} from "../models/definitions/QuestionClientName";
import {FormData, FormsRenderer} from "../services/FormsRenderer";
import {CodeBlock} from "../../../components/common/CodeBlock";
import {cloneSetup} from "../Utils";
import {ComposedForm} from "../models/definitions/ComposedForm";
import {TabPanel} from "../../../components/common/TabPanel";
import {CodeTextField} from "../../../components/common/input/CodeTextField";
import {json, jsonParseLinter} from "@codemirror/lang-json";
import {translateFunction} from "../../../localization/TranslationService";
import {GlobalState} from "../../../global/GlobalState";
import {dynamicFormsDefaultTargetStage, dynamicFormsTargetStage} from "../DynamicFormsOverviewPage";
import {HeaderSlot, StageSwitch} from "../../../components/onsite/Footer";

const translate = translateFunction("SureBasePortalDynamicForms");

function DynamicFormsFormDetails() {
	const formName = useParams()?.id;
	
	const targetStage = GlobalState.targetKiziStage(dynamicFormsDefaultTargetStage);
	const formDetails = useQuery(GET_FORM_DETAILS, {
		client: graphQLServices.dynamicFormsClient,
		fetchPolicy: "no-cache",
		variables: {
			stage: targetStage,
			formName
		}
	});
	
	if (formDetails.loading) {
		return <Box sx={{display: "flex", width: "100%", height: "100%"}}>
			<CircularProgress style={{margin: "auto"}}/>
		</Box>;
	}
	
	if (formDetails.error) {
		throw formDetails.error;
	}
	
	if (!formDetails.data) {
		throw new Error("Missing data!");
	}
	
	const forms: FormSetup[] = formDetails.data.formSetups.nodes
		.map(deepClone)
		.map(normalizeData);
	if (forms.length === 0)
		throw new Error(`Form with id ${formName} is not found.`);
	
	return <>
		<HeaderSlot><StageSwitch defaultTargetStage={dynamicFormsDefaultTargetStage}/></HeaderSlot>
		<PentaSplit
			top={<Breadcrumbs style={{margin: "5px"}}>
				<Link style={{color: "inherit", textDecoration: "unset"}}
					to="/dynamicforms">{translate("Dynamic Forms")}</Link>
				<Link style={{color: "inherit", textDecoration: "unset"}}
					to="/dynamicforms/forms">{translate("Forms")}</Link>
				<Typography color="text.primary">{formName}</Typography>
			</Breadcrumbs>}
			center={<DynamicFormsFormDetailsData
				initialForms={forms}
				targetStage={targetStage}
			/>}
		/>
	</>;
}

function defaultSuffix(initialForms: FormSetup[]): string {
	const name = initialForms[0].formName;
	if (name === "triple-play-order")
		return "/7df8ed90/1579";
	if (name === "healthcare-order")
		return "/7df8ed90/H228714460";
	
	return "";
}

function DynamicFormsFormDetailsData(props: { initialForms: FormSetup[]; targetStage: string }) {
	const [editorData, setEditorData] = useState(() => createEditorDataFromFormSetups(props.initialForms));
	const [suffix, setSuffix] = useState(defaultSuffix(props.initialForms));
	const [frontEnd, setFrontEnd] = useState<QuestionFrontEndName>("kizi");
	const [client, setClient] = useState<QuestionClientName>("kizi");
	
	function updateEditorData(data: DynamicFormsSetupEditorData<FormSetup>) {
		let previous: DynamicFormsSetupEditorData<FormSetup> | null = null;
		const newData = editorData.length === 1
			? [data]
			: editorData.map(it => {
				const result = it.setup.formId === data.setup.formId
					? data
					: it.copy({
						parentData: previous
					});
				previous = result;
				return result;
			});
		setEditorData(newData);
	}
	
	return <Allotment vertical={false}>
		<Allotment.Pane minSize={100}>
			<div style={{maxHeight: "100%", overflowY: "auto"}}>
				{editorData.map(data =>
					<Accordion key={data.setup.formId} defaultExpanded={true}>
						<AccordionSummary
							expandIcon={<ExpandMore/>}
						>
							<Typography>{translate("Form")} {data.setup.formId}</Typography>
						</AccordionSummary>
						<AccordionDetails>
							<DynamicFormsFormEditor
								data={data}
								update={updateEditorData}
								disabled={props.targetStage !== dynamicFormsTargetStage}
							/>
						</AccordionDetails>
					</Accordion>
				)}
			</div>
		</Allotment.Pane>
		<Allotment.Pane minSize={100}>
			<div style={{height: "100%", overflow: "auto"}}>
				<Stack>
					<Stack
						direction={"row"}
						sx={{
							"& > :not(style)": {m: "5px", width: "200px"},
						}}>
						<FormControl>
							<InputLabel>{translate("Front-End Name")}</InputLabel>
							<Select
								value={frontEnd}
								label={translate("Front-End Name")}
								onChange={event => setFrontEnd(event.target.value as QuestionFrontEndName)}
							>
								{questionFrontEndNames.map(it =>
									<MenuItem key={it} value={it}>{translate(it)}</MenuItem>
								)}
							</Select>
						</FormControl>
						
						<FormControl>
							<InputLabel>{translate("Client Name")}</InputLabel>
							<Select
								value={client}
								label={translate("Client Name")}
								onChange={event => setClient(event.target.value as QuestionClientName)}
							>
								{questionClientNames.map(it =>
									<MenuItem key={it} value={it}>{translate(it)}</MenuItem>
								)}
							</Select>
						</FormControl>
						
						<TextField
							label={"Suffix"}
							value={suffix}
							onChange={event => setSuffix(event.target.value)}
						/>
					</Stack>
				</Stack>
				<ErrorBoundary FallbackComponent={generalError} resetKeys={[editorData, frontEnd, client, suffix]}>
					<ComposedFormPreview
						setups={editorData.map(it => it.setup)}
						suffix={suffix}
						frontEnd={frontEnd}
						client={client}
					/>
				</ErrorBoundary>
			</div>
		</Allotment.Pane>
	</Allotment>;
}

interface ComposedFormPreviewProps {
	readonly setups: FormSetup[];
	readonly suffix: string;
	readonly frontEnd: string;
	readonly client: string;
}

function ComposedFormPreview(props: ComposedFormPreviewProps) {
	const setups = props.setups;
	
	const {loading, error, data} = useQuery(GET_FAKE_COMPOSED_FORM, {
		client: graphQLServices.dynamicFormsClient,
		fetchPolicy: "no-cache",
		variables: {
			stage: GlobalState.targetKiziStage(dynamicFormsDefaultTargetStage),
			setups: setups.map(cloneSetup),
			formName: setups[0].formName + props.suffix,
			frontEndName: props.frontEnd,
			clientName: props.client,
			clientVersion: 1,
			language: "nld",
			values: {}
		}
	});
	
	const [initialValues, setInitialValues] = useState<object>({});
	const [formData, setFormData] = useState<FormData>(() => new FormData(null, initialValues));
	
	const [tab, setTab] = useState(0);
	
	const composedForm: ComposedForm = data ? normalizeData(deepClone(data.fakeComposedForm)) : null;
	
	const renderer = new FormsRenderer();
	
	const formCode = JSON.stringify(composedForm, null, 4);
	
	/* eslint-disable react-hooks/exhaustive-deps */
	useEffect(() => {
		if (composedForm) {
			setFormData(new FormData(composedForm, initialValues));
		}
	}, [formCode, initialValues]);
	
	if (loading)
		return <Box sx={{display: "flex", width: "100%", height: "100%"}}>
			<CircularProgress style={{margin: "auto"}}/>
		</Box>;
	
	if (error)
		throw error;
	
	return <div style={{flexGrow: 1}}>
		<Stack>
			<div style={{flexGrow: 0}}>
				<Box sx={{borderBottom: 1, borderColor: "divider"}}>
					<Tabs value={tab} onChange={(event, newTab) => setTab(newTab)}>
						<Tab label={translate("Code")}/>
						<Tab label={translate("Preview")}/>
					</Tabs>
				</Box>
			</div>
			<div style={{flexGrow: 1}}>
				<TabPanel value={tab} index={0}>
					<CodeBlock name={translate("composed-definition.json")} syntax={json()}>{formCode}</CodeBlock>
				</TabPanel>
				<TabPanel value={tab} index={1}>
					<ErrorBoundary FallbackComponent={generalError}>
						<form onSubmit={event => event.preventDefault()}>
							<Stack
								alignItems={"stretch"}
								sx={{
									"& > :not(style)": {m: "5px"},
								}}>
								<CodeTextField
									maxHeight={"400px"}
									TextFieldProps={{
										label: "initial values",
										variant: "outlined",
										value: JSON.stringify(initialValues, null, 4),
										style: {flexGrow: 1},
										onChange: (event: any) => {
											try {
												const newValueJson: string = event.value;
												const newValue = JSON.parse(newValueJson);
												formData.initialValues = newValue;
												setInitialValues(newValue);
											} catch (e) {
												// ignore changes while json is not valid json
											}
										}
									}}
									syntax={json()}
									linter={jsonParseLinter()}
								/>
								{composedForm != null
									? <>
										{renderer.renderForm(composedForm, formData)}
										<div>
											<Button type="submit" variant="outlined">SEND</Button>
										</div>
									</>
									: undefined}
							</Stack>
						</form>
					</ErrorBoundary>
				</TabPanel>
			</div>
		</Stack>
	</div>;
}

function createEditorDataFromFormSetups(initialForms: FormSetup[]): DynamicFormsSetupEditorData<FormSetup>[] {
	// parent order:
	// (setups are assumed to be in the correct order by the graphql query)
	// (which is true as long as the "definition" fields are the same and no separate overrides are present)
	// definition:*:*
	// definition:frontend:*
	// definition:*:client
	// definition:frontend:client
	
	const newDef = newDefinition.get();
	const isNew = (setup: FormSetup) => newDef === setup.formName;
	
	let previous: DynamicFormsSetupEditorData<FormSetup> | null = null;
	return initialForms.map(it => {
		const result = new DynamicFormsSetupEditorData(it, it, previous, false, isNew(it));
		previous = result;
		return result;
	});
}

export const DynamicFormsFormDetailsPage = pageAuthorizedByGroup(DynamicFormsFormDetails, ["Admin", "DynamicForms"]);

export function openFormDetailsForNewDefinition(definition: string) {
	newDefinition.set(definition);
	openFormDetailsForDefinition(definition);
}

export function openFormDetailsForDefinition(definition: string) {
	window.location.href = `/dynamicforms/forms/${encodeURIComponent(definition)}`;
}
